import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../api";
import { SectionTitle } from "../../components/Atoms";
import { Palette, FloppyDisk, Eye, Sparkle, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { key: "app_name",          label: "App name",           placeholder: "Your workspace name", type: "text" },
  { key: "support_email",     label: "Support email",      placeholder: "support@yourbrand.com", type: "email" },
  { key: "login_heading",     label: "Login heading",      placeholder: "Welcome back to your workspace.", type: "text" },
  { key: "login_description", label: "Login description",  placeholder: "Manage creators, campaigns and payouts…", type: "textarea" },
];

// Small inline component: file picker + preview + delete for one asset field (logo/favicon).
function AssetUploader({ label, kind, value, onChange, size = 56 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => inputRef.current?.click();
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error("Max 6MB before conversion"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      const { data } = await api.post("/branding-assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success(`${label} uploaded (${Math.round(data.size / 1024)} KB WebP)`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="rounded-xl bg-white/5 border border-white/10 grid place-items-center overflow-hidden flex-shrink-0"
           style={{ width: size, height: size }}>
        {value ? (
          <img src={value} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="text-muted text-[10px]">Empty</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-sm">{label}</div>
        <div className="text-[11px] text-muted">Converts to .webp automatically to save space. Max 6MB.</div>
        <div className="mt-2 flex items-center gap-2">
          <button type="button" onClick={pick} disabled={busy}
                  className="btn-ghost text-xs flex items-center gap-1.5" data-testid={`upload-${kind}`}>
            <UploadSimple size={12} weight="bold" /> {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")}
                    className="btn-ghost text-xs flex items-center gap-1.5 text-[#EC4899]" data-testid={`remove-${kind}`}>
              <Trash size={12} weight="bold" /> Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

export default function ClientBrandingPage() {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [accent, setAccent] = useState("#3B82F6");
  const [secondary, setSecondary] = useState("#93C5FD");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations/me");
      setOrg(data);
      const b = data.branding || {};
      setForm({
        app_name: b.app_name || data.name || "",
        support_email: b.support_email || data.support_email || "",
        login_heading: b.login_heading || "",
        login_description: b.login_description || "",
        logo_url: b.logo_url || data.logo_url || "",
        favicon_url: b.favicon_url || "",
      });
      setAccent(b.accent_color || "#3B82F6");
      setSecondary(b.secondary_color || "#93C5FD");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/organizations/me/branding", {
        ...form, accent_color: accent, secondary_color: secondary,
      });
      setOrg(data);
      toast.success("Branding updated. Refresh public pages to see it applied.");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-muted text-sm">Loading…</div>;
  if (!org) return <div className="text-muted text-sm">No organization found for this account.</div>;

  // Public URL always jumps straight to the tenant's specific login variant
  // (Brand or Agency), so the shared link never shows the generic "Choose your
  // workspace" selector. Creators still use the main /login or /login/creator.
  const publicUrl = `${window.location.origin}/o/${org.slug}/login/${org.type || "brand"}`;

  return (
    <div className="space-y-6" data-testid="client-branding-page">
      <SectionTitle
        title="Workspace branding"
        subtitle="Your app name, logo, accent color and login copy. Applied instantly on your public login page and this dashboard."
        action={
          <button className="btn-primary flex items-center gap-2" disabled={busy} onClick={save} data-testid="branding-save">
            <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save branding"}
          </button>
        }
      />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* form */}
        <div className="lg:col-span-2 glass p-5 space-y-4">
          <div className="flex items-center gap-2 text-white font-bold">
            <UploadSimple size={18} weight="bold" /> Logo &amp; favicon
          </div>
          <AssetUploader label="Logo (400×400 max)" kind="logo" size={64}
                         value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} />
          <AssetUploader label="Favicon (64×64)" kind="favicon" size={44}
                         value={form.favicon_url} onChange={(url) => setForm({ ...form, favicon_url: url })} />

          <div className="pt-3 flex items-center gap-2 text-white font-bold border-t border-white/5">
            <Palette size={18} weight="bold" /> Colors
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted">Accent color</label>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={accent} onChange={e => setAccent(e.target.value)}
                       data-testid="branding-accent" className="h-10 w-16 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                <div className="text-xs text-muted font-mono">{accent}</div>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted">Secondary color</label>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={secondary} onChange={e => setSecondary(e.target.value)}
                       data-testid="branding-secondary" className="h-10 w-16 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                <div className="text-xs text-muted font-mono">{secondary}</div>
              </div>
            </div>
          </div>

          <div className="pt-3 flex items-center gap-2 text-white font-bold border-t border-white/5">
            <Sparkle size={16} weight="fill" /> Copy
          </div>
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-wider font-bold text-muted">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea rows={2} className="input-base mt-2" placeholder={f.placeholder}
                          value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                          data-testid={`branding-${f.key}`} />
              ) : (
                <input type={f.type} className="input-base mt-2" placeholder={f.placeholder}
                       value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                       data-testid={`branding-${f.key}`} />
              )}
            </div>
          ))}
        </div>

        {/* preview */}
        <div className="space-y-4">
          <div className="glass p-5 space-y-3">
            <div className="flex items-center gap-2 text-white font-bold">
              <Eye size={16} weight="bold" /> Live preview
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10"
                 style={{ background: `linear-gradient(135deg, ${accent}18 0%, ${secondary}18 100%)` }}>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-10 h-10 rounded-xl object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-xl grid place-items-center"
                         style={{ background: `linear-gradient(135deg, ${accent} 0%, ${secondary} 100%)` }}>
                      <Sparkle size={16} weight="fill" color="white" />
                    </div>
                  )}
                  <div className="text-white font-black" data-testid="branding-preview-name">{form.app_name || org.name}</div>
                </div>
                <div className="mt-4 text-white text-lg font-black tracking-tight">
                  {form.login_heading || "Welcome to your workspace."}
                </div>
                <div className="mt-1 text-xs text-muted">
                  {form.login_description || "Manage creators, campaigns and payouts in one place."}
                </div>
                <button type="button" className="mt-4 w-full py-2.5 rounded-full text-sm font-bold text-white"
                        style={{ background: `linear-gradient(90deg, ${accent} 0%, ${secondary} 100%)`,
                                 boxShadow: `0 8px 24px ${accent}55` }}>
                  Sign in
                </button>
              </div>
            </div>
          </div>

          <div className="glass p-5 space-y-2">
            <div className="text-white font-bold text-sm">Your public login link</div>
            <div className="text-[11px] text-muted">Share this with your team — branding auto-applies.</div>
            <div className="glass-soft px-3 py-2 text-xs font-mono text-white break-all" data-testid="branding-public-url">{publicUrl}</div>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1 mt-2"
               data-testid="branding-preview-link">
              Open in new tab →
            </a>
          </div>

          <div className="glass p-5 space-y-2">
            <div className="text-white font-bold text-sm">Your plan</div>
            <div className="glass-soft p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A3E635] to-[#22D3EE] grid place-items-center">
                <Sparkle size={16} weight="fill" color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-black text-sm">Free — launch access</div>
                <div className="text-[11px] text-muted">Unlimited projects &amp; creators during launch. Paid packages coming soon.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="glass-soft p-2">
                <div className="text-muted">Projects</div>
                <div className="text-white font-black">{org.usage?.projects ?? 0} <span className="text-muted font-normal">/ ∞</span></div>
              </div>
              <div className="glass-soft p-2">
                <div className="text-muted">Creators</div>
                <div className="text-white font-black">{org.usage?.creators ?? 0} <span className="text-muted font-normal">/ ∞</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

